<template>
    <layout-basic>

        <!-- Hero -->
        <section class="pp-hero">
            <v-container>
                <v-row justify="center" class="text-center">
                    <v-col cols="12" md="8">
                        <h1 class="font-sora pp-hero-title">IMPROVE Training Platform</h1>
                        <h2 class="font-sora pp-hero-sub">Partner Programme Overview</h2>
                        <p class="pp-tagline">Simple. Scalable. Partner-Ready.</p>
                        <p class="fs-18 mt-4"><strong>IMPROVE is a fully partner-ready Training Platform designed to support small to medium sized businesses (SMEs).</strong></p>
                        <p class="fs-18">We combine simplicity, scalability, and flexibility, allowing you to offer the critical training your clients need to protect their businesses. It's also a seamless way to induct employees into any business.</p>
                        <a href="/contact" class="pp-cta-btn">Become a Partner</a>
                    </v-col>
                </v-row>
            </v-container>
        </section>

        <!-- Partner logos -->
        <v-container>
            <section class="container-small">
                <v-row justify="center">
                    <v-col cols="12" class="text-center">
                        <p class="fs-18">Some of our partners</p>
                        <div class="partners-list">
                            <div class="single-partner"><img src="/static/company-logos/East-midlands-chamber.png" /></div>
                            <div class="single-partner"><img src="/static/company-logos/quest.jpg" /></div>
                            <div class="single-partner"><img src="/static/company-logos/RSM.png" /></div>
                            <div class="single-partner"><img src="/static/company-logos/Guardian-bussiness-services.png" /></div>
                            <div class="single-partner"><img src="/static/company-logos/HR-Central.png" /></div>
                        </div>
                    </v-col>
                </v-row>
            </section>
        </v-container>

        <!-- Key Platform Features -->
        <v-container>
            <section class="container-new">
                <v-row justify="center" class="text-center mb-6">
                    <v-col cols="12">
                        <h2 class="font-sora pp-section-title">Key Platform Features</h2>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" md="6" v-for="feature in platformFeatures" :key="feature.title" class="mb-4">
                        <div class="pp-feature-item">
                            <div class="pp-feature-icon">{{ feature.icon }}</div>
                            <div>
                                <h3 class="font-sora pp-feature-title">{{ feature.title }}</h3>
                                <p class="fs-16 mb-0">{{ feature.desc }}</p>
                            </div>
                        </div>
                        <v-divider class="mt-4"></v-divider>
                    </v-col>
                </v-row>
            </section>
        </v-container>

        <!-- Why Partner -->
        <section class="pp-why-partner">
            <v-container>
                <v-row justify="center" class="text-center mb-6">
                    <v-col cols="12">
                        <h2 class="font-sora pp-why-title">Why Partner with IMPROVE?</h2>
                    </v-col>
                </v-row>
                <v-row justify="center">
                    <v-col cols="12" md="5">
                        <ul class="pp-why-list">
                            <li v-for="item in whyLeft" :key="item">{{ item }}</li>
                        </ul>
                    </v-col>
                    <v-col cols="12" md="5">
                        <ul class="pp-why-list">
                            <li v-for="item in whyRight" :key="item">{{ item }}</li>
                        </ul>
                    </v-col>
                </v-row>
            </v-container>
        </section>

        <!-- What Makes Us Different -->
        <v-container>
            <section class="container-new">
                <v-row justify="center" class="text-center mb-6">
                    <v-col cols="12">
                        <h2 class="font-sora pp-section-title">What Makes Us Different?</h2>
                    </v-col>
                </v-row>
                <v-row justify="center">
                    <v-col cols="12" md="5">
                        <div class="pp-compare-box pp-compare-improve">
                            <div class="pp-compare-header">
                                <img src="/static/logo.png" class="pp-compare-logo" alt="IMPROVE" />
                            </div>
                            <ul class="pp-compare-list pp-compare-ticks">
                                <li v-for="item in improvePoints" :key="item">{{ item }}</li>
                            </ul>
                        </div>
                    </v-col>
                    <v-col cols="12" md="1" class="d-flex align-center justify-center">
                        <span class="pp-vs">VS</span>
                    </v-col>
                    <v-col cols="12" md="5">
                        <div class="pp-compare-box pp-compare-typical">
                            <div class="pp-compare-header">
                                <span class="font-sora pp-compare-name">Typical Training Platform / LMS Providers</span>
                            </div>
                            <ul class="pp-compare-list pp-compare-crosses">
                                <li v-for="item in typicalPoints" :key="item">{{ item }}</li>
                            </ul>
                        </div>
                    </v-col>
                </v-row>
            </section>
        </v-container>

        <!-- Testimonial -->
        <section class="pp-testimonial-section">
            <v-container>
                <v-row justify="center">
                    <v-col cols="12" md="8" class="text-center">
                        <p class="pp-testi-logo font-sora">Pure Human Resources</p>
                        <h3 class="font-sora pp-testi-quote">"Working with the IMPROVE team is so easy!"</h3>
                        <p class="fs-18 pp-testi-body">"They are really supportive in helping us get up to speed at short notice as a partner. We are now growing the client base we have using IMPROVE, and the product really does sell itself. The range of videos is great. The differentiator over other platforms is the API integration with other software and the ability for a client to record their own training materials or amend the quizzes and content to make the content really bespoke. I would definitely recommend the software and the support team."</p>
                    </v-col>
                </v-row>
            </v-container>
        </section>

        <!-- Course Categories -->
        <v-container>
            <section class="container-new">
                <v-row justify="center" class="text-center mb-6">
                    <v-col cols="12">
                        <h2 class="font-sora pp-section-title">Our Key Course Categories</h2>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" sm="4" v-for="cat in courseCategories" :key="cat.title" class="mb-4">
                        <div class="pp-course-card" :class="'pp-course-' + cat.color">
                            <h3 class="font-sora pp-course-title">{{ cat.title }}</h3>
                            <p class="pp-course-sub fw-600">{{ cat.subtitle }}</p>
                            <p class="fs-14 mb-0">{{ cat.desc }}</p>
                        </div>
                    </v-col>
                </v-row>
            </section>
        </v-container>

        <!-- Let's Partner CTA -->
        <section class="pp-lets-partner">
            <v-container>
                <v-row justify="center" class="text-center">
                    <v-col cols="12" md="8">
                        <h2 class="font-sora pp-cta-title">Let's Partner</h2>
                        <p class="fs-18 pp-cta-sub">Start generating new revenue streams with your own training platform.</p>
                        <p class="fs-18 pp-cta-contact">Contact us at: <a href="mailto:help@improve.online" class="pp-cta-email">help@improve.online</a></p>
                        <a href="/contact" class="pp-cta-btn pp-cta-btn-light">Get in Touch</a>
                    </v-col>
                </v-row>
            </v-container>
        </section>

    </layout-basic>
</template>

<script>
    import LayoutBasic from "@/layouts/Dashboard";
    import { IMPROVE_CONFIG } from "@/config.js";

    export default {
        name: "PartnerProgramme",
        metaInfo() {
            return {
                title: "Partner with IMPROVE: Simple, Scalable Training Platform for SMEs",
                meta: [
                    { name: 'description', content: "Partner with IMPROVE, a fully partner-ready training platform for SMEs. Zero upfront cost, simple pricing, HR integrations, and dedicated support. Start generating new revenue today." },
                    { name: 'keywords', content: "IMPROVE Partner Programme, SME training platform, partner-ready LMS, HR consultancy training, online training for SMEs, partner hub, co-branded training" },
                    { property: 'og:title', content: "Partner with IMPROVE: Simple, Scalable Training Platform for SMEs" },
                    { property: 'og:description', content: "Join the IMPROVE Partner Programme. Zero upfront cost, scalable pricing, full partner portal, and dedicated support to help you grow your client base." },
                    { property: 'og:image', content: "https://www.improve.online/static/seo.png" },
                    { property: 'og:type', content: "website" },
                    { property: 'og:url', content: "https://www.improve.online/partner-programme" },
                    { name: 'twitter:card', content: "summary_large_image" },
                    { name: 'twitter:title', content: "Partner with IMPROVE: Simple, Scalable Training Platform for SMEs" },
                    { name: 'twitter:description', content: "Partner with IMPROVE to offer SMEs flexible and branded training solutions. Simple pricing, HR integrations, and full partner enablement." },
                    { name: 'twitter:image', content: "https://www.improve.online/static/seo.png" }
                ],
                link: [
                    { rel: 'canonical', href: "https://www.improve.online/partner-programme" }
                ]
            };
        },
        components: { LayoutBasic },
        data() {
            return {
                signupUrl: IMPROVE_CONFIG.DIRECT_URL + '/#/signup?ref=website',
                platformFeatures: [
                    { icon: '📈', title: 'Built for Scale', desc: 'Scalable infrastructure to support your growing client base and their growing businesses.' },
                    { icon: '🖥', title: 'Proven Partner Portal', desc: 'Manage all clients, subscriptions, reporting and onboarding easily from a single platform.' },
                    { icon: '✏️', title: 'Custom Content Upload', desc: 'Add client-specific content, documents and courses to tailor training for every business.' },
                    { icon: '✅', title: 'Compliance & Core Content', desc: 'Access 100+ pre-built courses written by industry experts, covering GDPR, Cyber Security, Sexual Harassment and more.' },
                    { icon: '⭐', title: 'Simple User Experience', desc: 'Intuitive interface for learners, admins and partners — quick adoption with minimal training needed.' },
                    { icon: '📊', title: 'Insight Driven', desc: 'Full quick-view overviews alongside full reporting of staff progress and completion rates.' },
                    { icon: '❤️', title: 'Exceptional Support', desc: 'Dedicated partner success and technical support teams on hand whenever you need them.' },
                ],
                whyLeft: [
                    'Zero upfront cost to join',
                    'Fully partner-ready model',
                    'Fast setup — launch clients in minutes',
                    'Simple, scalable pricing models',
                ],
                whyRight: [
                    'Attractive revenue opportunity',
                    'Add company logos and customise courses',
                    'Co-branded marketing and onboarding support',
                    'Dedicated partner success manager',
                ],
                improvePoints: [
                    'Partner-first model',
                    'Customise the course',
                    'Simple pricing',
                    'HR API Integration',
                    'Full partner enablement',
                ],
                typicalPoints: [
                    'Direct-only models',
                    'Fixed content',
                    'Complicated fee structures',
                    'Limited integrations',
                    'Minimal partner support / Self-service only',
                ],
                courseCategories: [
                    { title: 'Compliance', subtitle: 'Workplace compliance courses.', desc: 'Workplace compliance ensures adherence to regulations and standards for your organisation.', color: 'green' },
                    { title: 'Compliance / Management', subtitle: 'Safeguard your organisation.', desc: 'Courses to help protect organisations from malicious intent, both intentional and unintentional.', color: 'dark' },
                    { title: 'Health & Safety', subtitle: 'Ensuring employee wellbeing.', desc: 'Health and safety training is essential to meet standards and mitigate risk in any business.', color: 'orange' },
                    { title: 'IT Skills', subtitle: 'Upskill your IT skills.', desc: 'Beginner, intermediate, and advanced courses on Word, Excel, PowerPoint, and Outlook.', color: 'blue' },
                    { title: 'People & Relationships', subtitle: 'Stronger workplace relationships.', desc: 'Courses on team working, wellbeing, customer service and how to improve workplace relationships.', color: 'purple' },
                    { title: 'Sales', subtitle: 'Increase your sales team\'s capabilities.', desc: 'Improve how your business promotes and sells products or services to customers or clients.', color: 'red' },
                    { title: 'Working from Home', subtitle: 'Tool kits for remote working.', desc: 'A range of comprehensive courses for individuals seeking to work more effectively at home.', color: 'red' },
                    { title: 'Project Management', subtitle: 'Effective project management.', desc: 'Courses to ensure organised workflow and successful outcomes in the workplace.', color: 'green' },
                    { title: 'Management & Leadership', subtitle: 'ILM-aligned leadership development.', desc: 'Develop core Leadership and Management Skills for Aspiring, First Line and Mid-Senior Level Managers and Directors in line with ILM standards. 55 interactive modules available as an optional bolt-on from £1,995 per business. Talk to us today.', color: 'dark' },
                ],
                pricingPlans: [
                    { users: '1-50 USERS', price: '£995' },
                    { users: '51-100 USERS', price: '£1,695' },
                    { users: '101-250 USERS', price: '£2,995' },
                ],
            };
        },
    };
</script>

<style scoped>
/* Hero */
.pp-hero {
    background: linear-gradient(135deg, #f8fffe 0%, #e8f8f4 100%);
    padding: 190px 0 60px;
}
.pp-hero-title { font-size: 2.4rem; color: #1a2e44; margin-bottom: 0; }
.pp-hero-sub { font-size: 1.6rem; color: #3a9e7e; margin-top: 4px; margin-bottom: 12px; }
.pp-tagline { font-size: 1.2rem; color: #555; letter-spacing: 0.05em; margin-bottom: 20px; }
.pp-cta-btn {
    display: inline-block;
    margin-top: 24px;
    padding: 14px 32px;
    background: #3a9e7e;
    color: #fff;
    border-radius: 6px;
    text-decoration: none;
    font-weight: 600;
    font-size: 1rem;
}
.pp-cta-btn:hover { background: #2d7d63; }

/* Section title */
.pp-section-title { font-size: 1.8rem; color: #1a2e44; margin-bottom: 8px; }

/* Features */
.pp-feature-item { display: flex; align-items: flex-start; gap: 16px; }
.pp-feature-icon { font-size: 1.8rem; flex-shrink: 0; margin-top: 2px; }
.pp-feature-title { font-size: 1.1rem; color: #1a2e44; margin-bottom: 4px; }

/* Why Partner */
.pp-why-partner {
    background: #1a2e44;
    padding: 60px 0;
    color: #fff;
}
.pp-why-title { font-size: 1.8rem; color: #fff; margin-bottom: 12px; }
.pp-why-list { list-style: none; padding: 0; margin: 0; }
.pp-why-list li {
    padding: 8px 0 8px 32px;
    position: relative;
    font-size: 1rem;
    color: #e0f7ef;
}
.pp-why-list li::before {
    content: '✔';
    position: absolute;
    left: 0;
    color: #3a9e7e;
    font-weight: bold;
}

/* What Makes Us Different */
.pp-compare-box {
    border-radius: 10px;
    padding: 24px;
    height: 100%;
}
.pp-compare-improve { border: 2px solid #3a9e7e; }
.pp-compare-typical { border: 2px solid #e0e0e0; background: #fafafa; }
.pp-compare-header { display: flex; align-items: center; gap: 10px; margin-bottom: 16px; }
.pp-compare-logo { height: 28px; width: auto; }
.pp-compare-name { font-size: 1rem; color: #1a2e44; }
.pp-compare-list { list-style: none; padding: 0; margin: 0; }
.pp-compare-list li { padding: 6px 0 6px 28px; position: relative; font-size: 0.95rem; border-bottom: 1px solid #eee; }
.pp-compare-list li:last-child { border-bottom: none; }
.pp-compare-ticks li::before { content: '✔'; position: absolute; left: 0; color: #3a9e7e; font-weight: bold; }
.pp-compare-crosses li::before { content: '✗'; position: absolute; left: 0; color: #e05252; font-weight: bold; }
.pp-vs { font-size: 1.4rem; font-weight: 700; color: #888; }

/* Testimonial */
.pp-testimonial-section { background: #f4f8f6; padding: 60px 0; }
.pp-testi-logo { font-size: 1.3rem; color: #1a2e44; font-weight: 700; margin-bottom: 8px; }
.pp-testi-quote { font-size: 1.3rem; color: #3a9e7e; margin-bottom: 16px; }
.pp-testi-body { color: #444; font-style: italic; }

/* Course Categories */
.pp-course-card {
    border-radius: 8px;
    padding: 20px;
    border: 1px solid #e0e0e0;
    height: 100%;
}
.pp-course-title { font-size: 1rem; margin-bottom: 4px; }
.pp-course-sub { font-size: 0.9rem; margin-bottom: 6px; }
.pp-course-green { border-color: #3a9e7e; }
.pp-course-green .pp-course-title { color: #3a9e7e; }
.pp-course-dark { border-color: #1a2e44; }
.pp-course-dark .pp-course-title { color: #1a2e44; }
.pp-course-orange { border-color: #e07a2a; }
.pp-course-orange .pp-course-title { color: #e07a2a; }
.pp-course-blue { border-color: #2a7ae0; }
.pp-course-blue .pp-course-title { color: #2a7ae0; }
.pp-course-red { border-color: #e05252; }
.pp-course-red .pp-course-title { color: #e05252; }
.pp-course-purple { border-color: #7c52e0; }
.pp-course-purple .pp-course-title { color: #7c52e0; }

/* Pricing */
.pp-price-card {
    border: 2px solid #3a9e7e;
    border-radius: 10px;
    padding: 32px 20px;
}
.pp-price-tier { color: #1a2e44; }
.pp-price-amount { font-size: 2.4rem; color: #3a9e7e; margin-bottom: 4px; }
.pp-price-desc { color: #777; margin-bottom: 16px; }
.pp-price-btn {
    display: inline-block;
    padding: 10px 24px;
    background: #3a9e7e;
    color: #fff;
    border-radius: 6px;
    text-decoration: none;
    font-weight: 600;
}
.pp-price-btn:hover { background: #2d7d63; }
.pp-enquire-link { color: #3a9e7e; font-weight: 600; }

/* Let's Partner CTA */
.pp-lets-partner {
    background: #1a2e44;
    padding: 60px 0;
    color: #fff;
}
.pp-cta-title { font-size: 2rem; color: #fff; margin-bottom: 8px; }
.pp-cta-sub { color: #c0d4e0; margin-bottom: 8px; }
.pp-cta-contact { color: #e0f7ef; margin-bottom: 20px; }
.pp-cta-email { color: #3a9e7e; font-weight: 600; text-decoration: none; }
.pp-cta-btn-light {
    background: #fff;
    color: #1a2e44;
    margin-top: 0;
}
.pp-cta-btn-light:hover { background: #e8f8f4; }
</style>
